/**
 * WalkthroughVerdictBlock — the signature "Would they return?" moment.
 *
 * Sentence-case kicker with the verdict as a toned chip, the verdict
 * sentence in sans, and an optional persona-voice quote in serif blue. This is the visual
 * anchor of any walkthrough summary surface (dashboard hero, walkthrough
 * detail landing, report PDF).
 *
 * The tone is derived from (status, rating) via `composeWalkthroughTone`
 * so non-terminal states (running/failed) get their own color without
 * the caller having to think about it.
 */

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Kicker } from "@/components/ui/kicker";
import { PersonaVoice } from "@/components/ui/persona-voice";
import { VerdictChip } from "@/components/walkthrough-badges";
import {
  verdictSentence,
  type VerdictRating,
  type WalkthroughStatus,
} from "./vocabulary";

/**
 * Literal union because Tailwind JIT needs the class names to appear
 * verbatim. Passing an arbitrary number would silently render nothing.
 */
type QuoteLineClamp = 2 | 3 | 4;

const quoteLineClampClass: Record<QuoteLineClamp, string> = {
  2: "line-clamp-2",
  3: "line-clamp-3",
  4: "line-clamp-4",
};

interface WalkthroughVerdictBlockProps {
  rating: VerdictRating | null;
  /**
   * Optional persona-voice reasoning — rendered as a blockquote below.
   * Pass a `<ReasoningText>` to resolve its finding and step references.
   */
  reasoning?: ReactNode;
  /** Walkthrough status — used to tint the kicker when not yet rated */
  status?: WalkthroughStatus;
  /** Clamp the persona quote at N lines (default: unclamped) */
  quoteLineClamp?: QuoteLineClamp;
  className?: string;
}

export function WalkthroughVerdictBlock({
  rating,
  reasoning,
  status = "completed",
  quoteLineClamp,
  className,
}: WalkthroughVerdictBlockProps) {
  if (!rating) return null;

  return (
    <div className={cn("max-w-[68ch] space-y-1.5", className)}>
      <div className="flex items-center gap-2">
        <Kicker>Would they return?</Kicker>
        <VerdictChip rating={rating} status={status} />
      </div>
      {!reasoning && (
        <p className="text-md font-semibold text-foreground">
          {verdictSentence(rating)}.
        </p>
      )}
      {reasoning && (
        <PersonaVoice
          size="sm"
          className={cn(
            "mt-2",
            quoteLineClamp && quoteLineClampClass[quoteLineClamp],
          )}
        >
          &ldquo;{reasoning}&rdquo;
        </PersonaVoice>
      )}
    </div>
  );
}
